import React from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import { useProductSettingsContext } from '../context/ProductSettingsContext';
import { PutProductDistributorFulfillmentType } from '../hooks/useFetchProductSettingsDistributorOverrides';
import { SkuFulfillmentMethod } from '../../../Enums/SkuFulfillmentMethod';
import { toYyyyMmDdInput, isSunday, maxInMarketDate } from '../utils/inMarketDate';

export const DistributorEndInMarketDatePicker = ({ row, handleUpdateRow }) => {
    const { getDistributorEndInMarketFloorDate, itemEndInMarketDate, showInMarketDateError, productDefaults } = useProductSettingsContext();
    const isReplenishable = row.skuFulfillmentType?.id === SkuFulfillmentMethod.REPLENISHABLE;
    const isOrderOnly = row.skuFulfillmentType?.id === SkuFulfillmentMethod.ORDER_ONLY;
    const inMarketDate = toYyyyMmDdInput(row.inMarketDateOverride);
    // The end date requires an in-market date on the same distributor row (requires start), and only
    // applies to seasonal SKUs.
    const disabled = row?.legal === false || (!isReplenishable && !isOrderOnly) || !inMarketDate || !productDefaults?.seasonal;
    const value = toYyyyMmDdInput(row.endInMarketDateOverride);
    const min = getDistributorEndInMarketFloorDate(row);
    const weekLimit = maxInMarketDate();
    // Tightest upper bound: the item's own end in-market date and the 52-week limit (date strings sort chronologically).
    const max = [itemEndInMarketDate, weekLimit].filter(Boolean).sort()[0];

    const handleChange = async (newValue) => {
        let apiDate;
        if (newValue === null) {
            apiDate = null;
        } else {
            if (!newValue.isValid()) return;
            const selected = newValue.format('YYYY-MM-DD');
            // Guard against typed input that bypasses the disabled calendar days.
            if (!isSunday(selected)) {
                showInMarketDateError('End in-market date must be a Sunday.');
                return;
            }
            if (min && selected < min) return;
            if (selected > max) return;
            apiDate = selected;
        }

        const updatedRow = {
            ...row,
            endInMarketDateOverride: apiDate ? `${apiDate}T00:00:00` : null
        };

        const result = await PutProductDistributorFulfillmentType(
            row.product,
            { ...updatedRow, skuFulfillmentType: row.skuFulfillmentType },
            null,
            { setEndInMarketDate: true, endInMarketDate: apiDate }
        );
        if (result.ok) {
            await handleUpdateRow(updatedRow);
        } else {
            showInMarketDateError(result.error || 'The end in-market date could not be saved.');
        }
    };

    return (
        <DatePicker
            value={value ? dayjs(value) : null}
            disabled={disabled}
            onChange={handleChange}
            minDate={min ? dayjs(min) : undefined}
            maxDate={dayjs(max)}
            shouldDisableDate={(date) => date.day() !== 0}
            slotProps={{
                textField: { size: 'small', fullWidth: true },
                field: { clearable: true }
            }}
        />
    );
};

DistributorEndInMarketDatePicker.propTypes = {
    row: PropTypes.object,
    handleUpdateRow: PropTypes.func
};
